import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { ProductService } from 'app/services/product/product.service';
import { LookupCacheService } from 'app/services/lookup-cache/lookup-cache.service';
import { CategoryService } from 'app/services/category/category.service';

import {
  IDropdownSettings,
  NgMultiSelectDropDownModule
} from 'ng-multiselect-dropdown';

import {
  FullProductDtoLight,
  LightStockDto
} from 'app/models/product-data-dto';

import { ImageItem } from 'app/services/gallery/gallery.service';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';

type LookupOption = {
  id: number | string;
  name: string;
};

type TabKey = 'basic' | 'price' | 'media';

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgMultiSelectDropDownModule,
    GalleryPickerComponent,
    ImageUrlPipe,
    ImageFallbackDirective
  ],
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.css']
})
export class EditProductComponent implements OnInit {
  productForm!: FormGroup;

  // ============ UI State (Signals) ============
  activeTab = signal<TabKey>('basic');
  submitted = signal(false);
  isLoading = signal(false);
  isSaving = signal(false);

  currentProductId = signal(0);
  productSlug = signal('');

  errorMessage = signal('');

  isGalleryModalOpen = signal(false);
  selectedPhotos = signal<ImageItem[]>([]);
  selectedPhotoIds = signal<number[]>([]);

  mainCategories = signal<LookupOption[]>([]);
  subCategories = signal<LookupOption[]>([]);
  brands = signal<LookupOption[]>([]);
  units = signal<LookupOption[]>([]);
  variants = signal<LookupOption[]>([]);
  stores = signal<LookupOption[]>([]);

  readonly tabs: { key: TabKey; labelAr: string; labelEn: string }[] = [
    { key: 'basic', labelAr: 'البيانات الأساسية', labelEn: 'Basic Info' },
    { key: 'price', labelAr: 'السعر والمخزون', labelEn: 'Price & Stock' },
    { key: 'media', labelAr: 'الوسائط والتهيئة', labelEn: 'Media & Settings' }
  ];

  readonly sources: LookupOption[] = [
    { id: 'main', name: 'رئيسي' },
    { id: 'sub', name: 'فرعي' }
  ];

  multiDropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'تحديد الكل',
    unSelectAllText: 'إلغاء الكل',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 4,
    closeDropDownOnSelection: false
  };

  singleDropdownSettings: IDropdownSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 1,
    closeDropDownOnSelection: true
  };

  private fieldLabelMap: { [key: string]: string } = {
    sourceType: 'نوع التصنيف',
    mainCategoryId: 'الفئة الرئيسية',
    subCategoryIds: 'الفئات الفرعية',
    brandId: 'البراند',
    parentId: 'Parent ID',
    status: 'الحالة',
    numOfSale: 'عدد المبيعات',
    storeId: 'المتجر',
    arName: 'اسم المنتج (عربي)',
    enName: 'اسم المنتج (إنجليزي)',
    arDescription: 'الوصف (عربي)',
    enDescription: 'الوصف (إنجليزي)',
    purchasePrice: 'سعر الشراء',
    sellingPrice: 'سعر البيع',
    sku: 'SKU',
    barcode: 'الباركود',
    published: 'منشور',
    approved: 'معتمد',
    isFeatured: 'منتج مميز',
    isTodaysDeal: 'عرض اليوم',
    isBestSeller: 'الأفضل مبيعًا',
    discount: 'الخصم',
    maxPurchaseQty: 'أقصى كمية شراء',
    variantId: 'المتغير',
    unitId: 'الوحدة',
    unitFactor: 'Unit Factor',
    barcodeType: 'Barcode Type',
    weightKg: 'الوزن (كجم)',
    minPurchaseQty: 'أقل كمية شراء',
    slug: 'Slug',
    photosIdsText: 'معرفات الصور',
    isNegativeStockEnable: 'السماح بالمخزون السالب',
    quantity: 'الكمية (داخل المخزون)',
    price: 'السعر (داخل المخزون)'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private lookupCacheService: LookupCacheService,
    private categoryService: CategoryService
  ) {}

  async ngOnInit(): Promise<void> {
    this.createForm();
    this.productSlug.set(this.route.snapshot.paramMap.get('slug') || '');
    await this.loadPageData();
    if (this.productSlug()) {
      await this.loadProductForEdit(this.productSlug());
    }
  }

  // ============ Getters for template ============
  get mainCategoryControl(): FormControl {
    return this.productForm.get('mainCategoryId') as FormControl;
  }

  get subCategoryControl(): FormControl {
    return this.productForm.get('subCategoryIds') as FormControl;
  }

  get brandControl(): FormControl {
    return this.productForm.get('brandId') as FormControl;
  }

  private createForm(): void {
    this.productForm = this.fb.group({
      sourceType: ['sub', Validators.required],
      mainCategoryId: [[], Validators.required],
      subCategoryIds: [[]],
      brandId: [[], Validators.required],
      parentId: [],
      status: [true],
      numOfSale: [0, [Validators.min(0)]],
      storeId: [1, Validators.required],
      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]],
      arDescription: [''],
      enDescription: [''],
      // purchasePrice غير مطلوب
      purchasePrice: [null],
      sellingPrice: [0, [Validators.required, Validators.min(0)]],
      // SKU غير مطلوب
      sku: [''],
      barcode: ['', Validators.required],
      stocks: this.fb.array([]),
      published: [true],
      approved: [false],
      isFeatured: [false],
      isTodaysDeal: [false],
      isBestSeller: [false],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      // maxPurchaseQty غير مطلوب، يقبل كسورًا
      maxPurchaseQty: [null],
      variantId: [null],
      unitId: [null, Validators.required],
      unitFactor: ['1', Validators.required],
      barcodeType: ['0', Validators.required],
      weightKg: [0, [Validators.min(0)]],
      // minPurchaseQty غير مطلوب، يقبل كسورًا
      minPurchaseQty: [null],
      slug: ['', Validators.required],
      photosIdsText: [''],
      isNegativeStockEnable: [false]
    });

    this.addStock();

    this.productForm.get('sourceType')?.valueChanges.subscribe(value => {
      if (value === 'main') {
        this.productForm.patchValue({ subCategoryIds: [] });
      }
    });
  }

  private async loadPageData(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set('');
    try {
      const lookups = await firstValueFrom(this.lookupCacheService.getLookups());
      this.brands.set(lookups.brands || []);
      this.units.set(lookups.units || []);
      this.variants.set(lookups.variants || []);
      this.stores.set(lookups.stores || []);

      await this.loadMainCategories();
    } catch (error) {
      console.error('Lookup loading error:', error);
      this.errorMessage.set('حدث خطأ أثناء تحميل بيانات الاختيارات.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async refreshLookups(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set('');
    try {
      const lookups = await firstValueFrom(this.lookupCacheService.refreshLookups());
      this.brands.set(lookups.brands || []);
      this.units.set(lookups.units || []);
      this.variants.set(lookups.variants || []);
      this.stores.set(lookups.stores || []);

      await this.loadMainCategories();
      alert('✅ تم تحديث بيانات الاختيارات بنجاح');
    } catch (error) {
      console.error('Refresh lookups error:', error);
      alert('❌ حدث خطأ أثناء تحديث بيانات الاختيارات');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadMainCategories(): Promise<void> {
    try {
      const data = await firstValueFrom(this.categoryService.getAllAdminMainCategories());
      this.mainCategories.set(data.map((cat: any) => ({
        id: cat.id,
        name: cat.name || `ID: ${cat.id}`
      })));
      console.log('✅ mainCategories updated:', this.mainCategories());
    } catch (e) {
      console.error('❌ Error loading main categories', e);
      this.mainCategories.set([]);
    }
  }

  private async loadSubCategoriesForParent(parentId: number): Promise<void> {
    try {
      const data = await firstValueFrom(this.categoryService.getAllAdminSubCategories(parentId));
      this.subCategories.set(data.map((cat: any) => ({
        id: cat.id,
        name: cat.name || `ID: ${cat.id}`
      })));
      console.log(`✅ subCategories for parent ${parentId} updated:`, this.subCategories());
    } catch (e) {
      console.error(`❌ Error loading sub categories for parent ${parentId}`, e);
      this.subCategories.set([]);
    }
  }

  private async loadProductForEdit(slug: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set('');
    try {
      const res: any = await firstValueFrom(this.productService.getProductBySlug(slug));
      console.log(res);
      const product = res?.data?.[0];
      if (!res?.success || !product) {
        this.errorMessage.set(res?.message || 'المنتج غير موجود.');
        return;
      }
      this.currentProductId.set(Number(product.id));
      await this.patchProduct(product);
    } catch (error) {
      console.error('Product loading error:', error);
      this.errorMessage.set('حدث خطأ أثناء تحميل بيانات المنتج.');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async patchProduct(product: FullProductDtoLight): Promise<void> {
    console.log(product);
    this.patchStocks(product);

    const mainCategoryId = product.main_category_id ?? null;
    const brandId = product.brand_id ?? null;
    const variantId = product.variant_id ?? null;
    const unitId = product.unit_id ?? null;
    const subIds = this.extractSubCategoryIds(product);
    const photoIds = this.extractPhotoIds(product);

    if (mainCategoryId) {
      await this.loadSubCategoriesForParent(Number(mainCategoryId));
    }

    this.selectedPhotoIds.set(photoIds);
    this.selectedPhotos.set(
      (product.photos_urls || []).map((url: string, index: number) => ({
        id: photoIds[index] ?? 0,
        name: url.split('/').pop() || 'image',
        url: url.startsWith('/') ? url : '/' + url,
        path: url,
        size: 0,
        date: ''
      } as ImageItem))
    );

    const mainCategorySelection = this.optionArrayById(this.mainCategories(), mainCategoryId);
    const brandSelection = this.optionArrayById(this.brands(), brandId);
    const subCategorySelection = this.optionsArrayByIds(this.subCategories(), subIds);

    // معالجة القيم الاختيارية
    const purchasePrice = product.purchase_price != null ? Number(product.purchase_price) : null;
    const maxPurchaseQty = product.max_purchase_qty != null ? Number(product.max_purchase_qty) : null;
    const minPurchaseQty = product.min_purchase_qty != null ? Number(product.min_purchase_qty) : null;

    this.productForm.patchValue({
      sourceType: product.source_type ?? 'sub',
      mainCategoryId: mainCategorySelection,
      subCategoryIds: subCategorySelection,
      brandId: brandSelection,
      parentId: product.parent_id,
      status: this.toBool(product.status, true),
      numOfSale: Number(product.num_of_sale ?? 0),
      isBestSeller: this.toBool(product.is_best_seller, false),
      discount: Number(product.discount ?? 0),
      maxPurchaseQty: maxPurchaseQty,
      storeId: Number(product.store_id ?? 1),
      arName: product.ar_name ?? '',
      enName: product.en_name ?? '',
      arDescription: product.ar_description ?? '',
      enDescription: product.en_description ?? '',
      purchasePrice: purchasePrice,
      sellingPrice: Number(product.selling_price ?? 0),
      sku: product.sku ?? '',
      barcode: product.barcode ?? '',
      published: this.toBool(product.published, true),
      approved: this.toBool(product.approved, false),
      isFeatured: this.toBool(product.is_featured, false),
      isTodaysDeal: this.toBool(product.is_todays_deal, false),
      variantId: variantId && Number(variantId) > 0 ? Number(variantId) : null,
      unitId: unitId ? unitId.toString() : null,
      unitFactor: (product.unit_factor ?? '1').toString(),
      barcodeType: (product.barcode_type ?? '0').toString(),
      weightKg: Number(product.weight_kg ?? 0),
      minPurchaseQty: minPurchaseQty,
      slug: product.slug ?? '',
      photosIdsText: photoIds.join(','),
      isNegativeStockEnable: this.toBool(product.is_nagtive_stock_enable, false)
    });
    console.log('✅ Product patched:', this.productForm.value);
  }

  private patchStocks(product: FullProductDtoLight): void {
    const sourceStocks = product.stocks ?? [];
    this.stocks.clear();
    if (!Array.isArray(sourceStocks) || sourceStocks.length === 0) {
      this.addStock({
        store_id: Number(product.store_id ?? 1),
        quantity: 0,
        price: Number(product.selling_price ?? 0)
      });
      return;
    }
    sourceStocks.forEach((item: LightStockDto) => {
      this.addStock({
        store_id: Number(item.store_id ?? 1),
        quantity: Number(item.quantity ?? 0),
        price: Number(item.price ?? product.selling_price ?? 0)
      });
    });
  }

  get stocks(): FormArray {
    return this.productForm.get('stocks') as FormArray;
  }

  private createStockGroup(data?: LightStockDto): FormGroup {
    return this.fb.group({
      storeId: [Number(data?.store_id ?? 1), Validators.required],
      quantity: [Number(data?.quantity ?? 0), [Validators.min(0)]],
      price: [Number(data?.price ?? 0), [Validators.min(0)]]
    });
  }

  addStock(data?: LightStockDto): void {
    this.stocks.push(this.createStockGroup(data));
  }

  removeStock(index: number): void {
    if (this.stocks.length <= 1) return;
    this.stocks.removeAt(index);
  }

  setActiveTab(tabKey: TabKey): void {
    this.activeTab.set(tabKey);
  }

  nextTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab());
    if (currentIndex < this.tabs.length - 1) {
      this.activeTab.set(this.tabs[currentIndex + 1].key);
    }
  }

  previousTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab());
    if (currentIndex > 0) {
      this.activeTab.set(this.tabs[currentIndex - 1].key);
    }
  }

  isFirstTab(): boolean {
    return this.activeTab() === this.tabs[0].key;
  }

  isLastTab(): boolean {
    return this.activeTab() === this.tabs[this.tabs.length - 1].key;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.productForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty || this.submitted()));
  }

  isDropdownInvalid(fieldName: string): boolean {
    const value = this.productForm.get(fieldName)?.value;
    return !!(this.submitted() && (!Array.isArray(value) || value.length === 0));
  }

  getFieldError(fieldName: string): string {
    const field = this.productForm.get(fieldName);
    if (!field || !field.errors) return '';
    if (field.errors['required']) return 'هذا الحقل مطلوب';
    if (field.errors['min']) return `القيمة يجب ألا تقل عن ${field.errors['min'].min}`;
    if (field.errors['max']) return `القيمة يجب ألا تزيد عن ${field.errors['max'].max}`;
    if (field.errors['minlength']) return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
    return 'قيمة غير صحيحة';
  }

  onSubmit(): void {
    this.submitted.set(true);
    this.markFormGroupTouched(this.productForm);
    this.logFormErrors();

    if (!this.validateFormBeforeSubmit()) {
      return;
    }

    const dto = this.buildUpdateDto();
    console.log('Update DTO:', dto);
    this.updateProduct(dto);
  }

  private logFormErrors(): void {
    const errors: string[] = [];
    const collectErrors = (control: AbstractControl, path: string) => {
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(key => {
          collectErrors(control.get(key)!, `${path}.${key}`);
        });
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl, index) => {
          collectErrors(ctrl, `${path}[${index}]`);
        });
      } else if (control.invalid) {
        const fieldKey = path.split('.').pop() || path;
        const label = this.fieldLabelMap[fieldKey] || fieldKey;
        const errMsgs = control.errors
          ? Object.keys(control.errors).map(k => `${k}: ${JSON.stringify(control.errors![k])}`)
          : [];
        console.warn(`❌ حقل غير صالح: ${label}`, errMsgs, control.errors);
        errors.push(`${label} (${errMsgs.join(', ')})`);
      }
    };
    collectErrors(this.productForm, 'productForm');
    if (errors.length) {
      console.log('📋 مجمل الأخطاء:', errors);
    } else {
      console.log('✅ جميع الحقول صالحة.');
    }
  }

  private getFormErrorsList(): string[] {
    const messages: string[] = [];
    const controls = this.productForm.controls;

    for (const key in controls) {
      if (controls[key].invalid) {
        const label = this.fieldLabelMap[key] || key;
        const errs = controls[key].errors;
        if (errs?.['required']) {
          messages.push(`${label} مطلوب.`);
        } else if (errs?.['minlength']) {
          messages.push(`${label} يجب ألا يقل عن ${errs['minlength'].requiredLength} حروف.`);
        } else if (errs?.['min']) {
          messages.push(`${label} يجب ألا يقل عن ${errs['min'].min}.`);
        } else if (errs?.['max']) {
          messages.push(`${label} يجب ألا يزيد عن ${errs['max'].max}.`);
        } else {
          messages.push(`${label} غير صالح.`);
        }
      }
    }

    this.stocks.controls.forEach((stock, i) => {
      const stockGroup = stock as FormGroup;
      if (stockGroup.invalid) {
        const stockErrors: string[] = [];
        if (stockGroup.get('storeId')?.invalid) stockErrors.push('المتجر');
        if (stockGroup.get('quantity')?.invalid) stockErrors.push('الكمية');
        if (stockGroup.get('price')?.invalid) stockErrors.push('السعر');
        if (stockErrors.length) {
          messages.push(`المخزون ${i + 1}: ${stockErrors.join('، ')} غير صالح.`);
        }
      }
    });

    return messages;
  }

  private validateFormBeforeSubmit(): boolean {
    const errors: string[] = [];

    const formErrors = this.getFormErrorsList();
    if (formErrors.length) {
      errors.push(...formErrors);
    }

    const mainCategoryId = this.getSingleDropdownId(this.productForm.value.mainCategoryId);
    if (!mainCategoryId) {
      errors.push('الفئة الرئيسية مطلوبة.');
    }

    const brandId = this.getSingleDropdownId(this.productForm.value.brandId);
    if (!brandId) {
      errors.push('البراند مطلوب.');
    }

    if (this.stocks.length === 0) {
      errors.push('يجب إضافة مخزن واحد على الأقل.');
    }

    const storeIds = this.stocks.value
      .map((x: any) => Number(x.storeId))
      .filter((x: number) => Number.isFinite(x));
    const hasDuplicateStores = storeIds.some(
      (id: number, index: number) => storeIds.indexOf(id) !== index
    );
    if (hasDuplicateStores) {
      errors.push('لا يمكن تكرار نفس المتجر داخل المخزون.');
    }

    if (errors.length > 0) {
      alert('❌ ' + errors.join('\n'));
      return false;
    }
    return true;
  }

  private buildUpdateDto(): any {
    const v = this.productForm.value;
    const mainCategoryId = this.getSingleDropdownId(v.mainCategoryId);
    const brandId = this.getSingleDropdownId(v.brandId);
    const subCategoryIds = this.getMultiDropdownIds(v.subCategoryIds);
    const stocks = (v.stocks || []).map((item: any) => ({
      storeId: item.storeId?.toString(),
      quantity: Number(item.quantity ?? 0),
      price: Number(item.price ?? 0)
    }));

    // معالجة الحقول الاختيارية: إذا كانت فارغة نرسل null
    const purchasePrice = (v.purchasePrice !== null && v.purchasePrice !== '' && v.purchasePrice !== undefined)
      ? Number(v.purchasePrice)
      : null;
    const maxPurchaseQty = (v.maxPurchaseQty !== null && v.maxPurchaseQty !== '' && v.maxPurchaseQty !== undefined)
      ? Number(v.maxPurchaseQty)
      : null;
    const minPurchaseQty = (v.minPurchaseQty !== null && v.minPurchaseQty !== '' && v.minPurchaseQty !== undefined)
      ? Number(v.minPurchaseQty)
      : null;

    return {
      sourceType: v.sourceType,
      mainCategoryId: mainCategoryId?.toString() ?? null,
      subCategoryIds,
      parentId: v.parentId ? Number(v.parentId) : null,
      brandId: brandId ? Number(brandId) : null,
      status: !!v.status,
      numOfSale: Number(v.numOfSale ?? 0),
      storeId: v.storeId ? Number(v.storeId) : null,
      arName: v.arName ?? '',
      enName: v.enName ?? '',
      arDescription: v.arDescription ?? '',
      enDescription: v.enDescription ?? '',
      purchasePrice: purchasePrice,
      sellingPrice: Number(v.sellingPrice ?? 0),
      sku: v.sku ?? '',
      barcode: v.barcode ?? '',
      stocks,
      discount: Number(v.discount ?? 0),
      maxPurchaseQty: maxPurchaseQty,
      isBestSeller: !!v.isBestSeller,
      published: !!v.published,
      approved: !!v.approved,
      isFeatured: !!v.isFeatured,
      isTodaysDeal: !!v.isTodaysDeal,
      variantId: v.variantId ? Number(v.variantId) : null,
      unitId: v.unitId?.toString() ?? null,
      unitFactor: v.unitFactor?.toString() ?? '1',
      barcodeType: v.barcodeType?.toString() ?? '0',
      weightKg: Number(v.weightKg ?? 0),
      minPurchaseQty: minPurchaseQty,
      slug: v.slug ?? '',
      photos_ids: this.parseIdsText(v.photosIdsText),
      isNagtiveStockEnable: !!v.isNegativeStockEnable
    };
  }

  private updateProduct(dto: any): void {
    if (!this.currentProductId()) {
      alert('❌ لا يوجد Product ID للتحديث.');
      return;
    }
    this.isSaving.set(true);
    this.productService.updateProduct(this.currentProductId(), dto).subscribe({
      next: (response: any) => {
        console.log('Update response:', response);
        alert('✅ تم تحديث المنتج بنجاح');
        this.router.navigate(['/admin/products']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Update error:', error);
        this.handleError(error);
        this.isSaving.set(false);
      },
      complete: () => {
        this.isSaving.set(false);
      }
    });
  }

  onReset(): void {
    if (!this.productSlug()) return;
    if (confirm('هل تريد إعادة تحميل بيانات المنتج من جديد؟')) {
      this.loadProductForEdit(this.productSlug());
    }
  }

  onCancel(): void {
    if (confirm('هل تريد إلغاء التعديلات؟')) {
      this.router.navigate(['/admin/products']);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.error?.errors) {
      console.error('Validation errors:', error.error.errors);
      alert('❌ يوجد أخطاء في البيانات. راجع Console.');
      return;
    }
    if (error.error?.message) {
      alert('❌ ' + error.error.message);
      return;
    }
    alert('❌ حدث خطأ أثناء تحديث المنتج.');
  }

  private optionArrayById(list: LookupOption[], id: any): LookupOption[] {
    if (id === null || id === undefined || id === '') return [];
    const found = list.find(item => Number(item.id) === Number(id));
    if (found) return [found];
    return [{ id: Number(id), name: `#${id}` }];
  }

  private optionsArrayByIds(list: LookupOption[], ids: number[]): LookupOption[] {
    return ids
      .filter(id => Number.isFinite(Number(id)) && Number(id) > 0)
      .map(id => {
        const found = list.find(item => Number(item.id) === Number(id));
        return found || { id, name: `#${id}` };
      });
  }

  private getSingleDropdownId(value: any): number | null {
    if (Array.isArray(value)) {
      const first = value[0];
      if (!first) return null;
      return Number(first.id ?? first);
    }
    if (value && typeof value === 'object') {
      return Number(value.id);
    }
    if (value === null || value === undefined || value === '') return null;
    return Number(value);
  }

  private getMultiDropdownIds(value: any): number[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => Number(item?.id ?? item))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private extractSubCategoryIds(product: FullProductDtoLight): number[] {
    const raw = product.sub_category_ids ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map(id => Number(id)).filter(id => Number.isFinite(id) && id > 0);
  }

  private extractPhotoIds(product: FullProductDtoLight): number[] {
    const raw = product.photos_ids ?? [];
    if (Array.isArray(raw)) {
      return raw.map(id => Number(id)).filter(id => Number.isFinite(id) && id > 0);
    }
    return [];
  }

  private parseIdsText(value: string): number[] {
    if (!value) return [];
    return value
      .split(',')
      .map(x => Number(x.trim()))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private toBool(value: any, fallback: boolean): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true') return true;
    if (value === false || value === 0 || value === '0' || value === 'false') return false;
    return fallback;
  }

  // ============ أحداث القوائم المنسدلة ============
  onMainCategorySelect(event: any): void {
    const selectedId = this.getSingleDropdownId(event);
    if (selectedId) {
      this.loadSubCategoriesForParent(selectedId);
      this.productForm.patchValue({ subCategoryIds: [] });
    }
  }

  onMainCategoryDeSelect(): void {
    this.subCategories.set([]);
    this.productForm.patchValue({ subCategoryIds: [] });
  }

  onBrandSelect(_: any): void {}
  onBrandDeSelect(_: any): void {}

  onSubCategorySelect(_: any): void {}
  onSubCategoryDeSelect(_: any): void {}

  // ============ المعرض ============
  openGalleryModal(): void {
    this.isGalleryModalOpen.set(true);
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen.set(false);
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    const photos = images || [];
    this.selectedPhotos.set(photos);
    this.selectedPhotoIds.set(
      photos.map(x => Number(x.id)).filter(id => Number.isFinite(id) && id > 0)
    );
    this.patchPhotosIdsToForm();
  }

  removeSelectedPhoto(photo: ImageItem): void {
    const remaining = this.selectedPhotos().filter(x => Number(x.id) !== Number(photo.id));
    this.selectedPhotos.set(remaining);
    this.selectedPhotoIds.set(
      remaining.map(x => Number(x.id)).filter(id => Number.isFinite(id) && id > 0)
    );
    this.patchPhotosIdsToForm();
  }

  isGalleryImageSelected(image: ImageItem): boolean {
    return this.selectedPhotoIds().includes(Number(image.id));
  }

  private patchPhotosIdsToForm(): void {
    this.productForm.patchValue({
      photosIdsText: this.selectedPhotoIds().join(',')
    });
    this.productForm.get('photosIdsText')?.markAsDirty();
    this.productForm.get('photosIdsText')?.updateValueAndValidity();
  }
}